/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */

/* eslint-disable no-param-reassign,no-console */
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import * as Sentry from "@sentry/react";
import { Scope } from "@sentry/react";
import { Integrations } from "@sentry/tracing";
import * as url from "url";
import redact from "redact-object";
import { Primitive } from "@sentry/types";
import { APP_ENVIRONMENT, APP_RELEASE, SENTRY_DSN } from "./constants";
import { SentryFilterStatus } from "../constant";
import { ErrorMessage } from "../types/components/errorMessage";

const sensitiveKeys = [
  "token",
  "secret",
  "accessToken",
  "access_token",
  "access-token",
  "refreshToken",
  "refresh_token",
  "refresh-token",
  "code",
  "authorization_code",
  "password",
  "keyword",
];

const scrubUrlParams = (urlString: string, paramsToScrub = sensitiveKeys) => {
  const parts = url.parse(urlString, true);
  const { query } = parts;
  const queryKeys = Object.keys(query);
  queryKeys.forEach((param) => {
    if (paramsToScrub.includes(param)) {
      query[param] = "***";
    }
  });
  parts.query = query;
  parts.search = null;
  return url.format(parts);
};

export const initializeSentry = () => {
  if (SENTRY_DSN) {
    try {
      Sentry.init({
        environment: APP_ENVIRONMENT,
        dsn: SENTRY_DSN,
        autoSessionTracking: true,
        integrations: [new Integrations.BrowserTracing()],
        // We recommend adjusting this value in production, or using tracesSampler for finer control
        tracesSampleRate: 1.0,
        release: APP_RELEASE.replaceAll("/", "_"),
        beforeSend: (event, hint) => {
          if (event) {
            const { exception } = event;
            const error = hint.originalException as unknown as ErrorMessage;
            const status = error?.response?.status;
            const ignoreStatusesCode = SentryFilterStatus.includes(status);
            const ignoreReduxError = error?.message?.includes(
              "Minified Redux error"
            );

            const ignoreResizeObjectMessage =
              // @ts-ignore
              error?.message?.toLowerCase()?.includes("resizeobserver");

            if (ignoreReduxError || ignoreResizeObjectMessage) return null; // ignore redux minified error.

            // filter out refresh 401
            const isRefreshToken =
              error?.response?.config?.url === "/auth/refresh" &&
              status === 401;
            if (
              error?.isAxiosError && // belong axios
              (!error?.response || // unexpected error. 503, 504
                ignoreStatusesCode || // ignore status
                isRefreshToken || // refresh token.
                error?.code === "ECONNABORTED" || // timeout request
                error?.message === "Network Error") // No internet connection, CORS , DNS lookup failures, Invalid request URL or protocol mismatch, Request blocked by browser security policies
            ) {
              return null;
            }
            if (exception) {
              const { values } = exception;
              // bypass the non-error event
              if (values && values[0].type === "UnhandledRejection") {
                return null;
              }
            }
          }
          return event;
        },
        beforeBreadcrumb: (breadcrumb: Sentry.Breadcrumb) => {
          const { data, category } = breadcrumb;
          if (category === "console") {
            breadcrumb.data = redact(data, sensitiveKeys);
          }

          if (category === "xhr" && data) {
            data.url = scrubUrlParams(data.url);
          }

          return breadcrumb;
        },
      });
    } catch (error) {
      console.debug("Initialized Sentry failed", error);
    }
  } else {
    console.debug("Cannot find sentry dsn from env");
  }
};

export const setSentryApplicationContext = () => {
  Sentry.setContext("application", {
    ui: APP_RELEASE,
    environment: APP_ENVIRONMENT,
  });
};

export const setSentryTags = (tags: { [key: string]: Primitive }) => {
  Sentry.setTags(tags);
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const captureExceptionToSentry = (exception: any, context: Scope) => {
  Sentry.captureException(exception, context);
};

export const captureEventToSentry = (event: Sentry.Event) => {
  Sentry.captureEvent(event);
};
